<template>
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-3xl font-bold text-gray-900">Add Manual Transaction</h1>
      <a :href="routes.index" class="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg">
        <i class="fas fa-arrow-left mr-2"></i> Back to Transactions
      </a>
    </div>

    <!-- Form -->
    <div class="bg-white rounded-card shadow-xl">
      <div class="px-6 py-4 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900">Transaction Details</h2>
      </div>
      
      <form @submit.prevent="submitForm" class="p-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Company -->
          <div>
            <label for="company_id" class="block text-sm font-medium text-gray-700 mb-2">
              Company <span class="text-gray-400">(optional)</span>
            </label>
            <div class="relative">
              <input
                v-model="companySearchTerm"
                @focus="showCompanyDropdown = true"
                @blur="handleCompanyBlur"
                @input="filterCompanies"
                @keydown.enter.prevent="selectFirstCompanyMatch"
                @keydown.escape="closeCompanyDropdown"
                @keydown.arrow-down.prevent="navigateCompanyDown"
                @keydown.arrow-up.prevent="navigateCompanyUp"
                type="text"
                id="company_id"
                placeholder="Search company..."
                class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              
              <div 
                v-if="showCompanyDropdown && filteredCompanies.length > 0"
                class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto"
              >
                <button
                  v-for="(company, index) in filteredCompanies"
                  :key="company.id"
                  @mousedown.prevent="selectCompany(company)"
                  type="button"
                  :class="[
                    'w-full text-left px-3 py-2 text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none',
                    index === highlightedCompanyIndex ? 'bg-blue-100' : ''
                  ]"
                >
                  {{ company.name }}
                </button>
              </div>
              
              <div 
                v-if="showCompanyDropdown && filteredCompanies.length === 0 && companySearchTerm"
                class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg px-3 py-2 text-sm text-gray-500"
              >
                No companies found
              </div>
            </div>
            <p class="mt-1 text-sm text-gray-500">
              Leave empty if company is unknown. Required for auto-mapping commissions.
            </p>
          </div>

          <!-- Amount -->
          <div>
            <label for="amount" class="block text-sm font-medium text-gray-700 mb-2">
              Amount *
            </label>
            <div class="relative">
              <input
                v-model="form.amount"
                type="number"
                step="0.01"
                min="0"
                id="amount"
                required
                class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pl-8"
                placeholder="0.00"
              />
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span class="text-gray-500 sm:text-sm">€</span>
              </div>
            </div>
          </div>

          <!-- Currency -->
          <div>
            <label for="currency" class="block text-sm font-medium text-gray-700 mb-2">
              Currency *
            </label>
            <select
              v-model="form.currency"
              id="currency"
              required
              class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="EUR">EUR - Euro</option>
              <option value="USD">USD - US Dollar</option>
              <option value="SEK">SEK - Swedish Krona</option>
              <option value="GBP">GBP - British Pound</option>
            </select>
          </div>

          <!-- VAT Included -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              VAT Status *
            </label>
            <div class="space-y-2">
              <label class="flex items-center">
                <input
                  v-model="form.include_vat"
                  type="radio"
                  :value="true"
                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span class="ml-2 text-sm text-gray-700">Amount includes VAT (25%)</span>
              </label>
              <label class="flex items-center">
                <input
                  v-model="form.include_vat"
                  type="radio"
                  :value="false"
                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span class="ml-2 text-sm text-gray-700">Amount is net (exclude VAT)</span>
              </label>
            </div>
            <p class="mt-1 text-sm text-gray-500">
              Commissions are calculated on net amount (excluding VAT)
            </p>
          </div>

          <!-- Payment Method -->
          <div>
            <label for="payment_method" class="block text-sm font-medium text-gray-700 mb-2">
              Payment Method *
            </label>
            <select
              v-model="form.payment_method"
              id="payment_method"
              required
              class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select payment method...</option>
              <option value="invoice">Invoice</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="card">Card Payment</option>
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="other">Other</option>
            </select>
          </div>

          <!-- Product Description -->
          <div class="md:col-span-2">
            <label for="product_description" class="block text-sm font-medium text-gray-700 mb-2">
              Product/Service Description *
            </label>
            <textarea
              v-model="form.product_description"
              id="product_description"
              rows="3"
              required
              class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Describe what was purchased..."
            ></textarea>
          </div>

          <!-- Customer Email -->
          <div>
            <label for="customer_email" class="block text-sm font-medium text-gray-700 mb-2">
              Customer Email <span class="text-gray-400">(optional)</span>
            </label>
            <input
              v-model="form.customer_email"
              type="email"
              id="customer_email"
              class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="customer@example.com"
            />
            <p class="mt-1 text-sm text-gray-500">
              Used for user matching and records
            </p>
          </div>

          <!-- Customer Name -->
          <div>
            <label for="customer_name" class="block text-sm font-medium text-gray-700 mb-2">
              Customer Name <span class="text-gray-400">(optional)</span>
            </label>
            <input
              v-model="form.customer_name"
              type="text"
              id="customer_name"
              class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Customer Name"
            />
          </div>

          <!-- Transaction Date -->
          <div class="md:col-span-2">
            <label for="transaction_date" class="block text-sm font-medium text-gray-700 mb-2">
              Transaction Date *
            </label>
            <input
              v-model="form.transaction_date"
              type="date"
              id="transaction_date"
              required
              class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        <!-- Submit Button -->
        <div class="mt-8 flex justify-end space-x-4">
          <a :href="routes.index" class="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-lg font-medium">
            Cancel
          </a>
          <button
            type="submit"
            :disabled="submitting"
            class="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-6 py-2 rounded-lg font-medium"
          >
            <span v-if="submitting">Creating...</span>
            <span v-else>Create Transaction</span>
          </button>
        </div>
      </form>
    </div>

    <!-- Commission Preview -->
    <div v-if="form.amount && form.include_vat !== null" class="mt-6 bg-blue-50 rounded-card p-6">
      <h3 class="text-lg font-semibold text-blue-900 mb-4">Commission Preview</h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div>
          <div class="font-medium text-blue-800">Total Amount</div>
          <div class="text-blue-600">{{ formatCurrency(parseFloat(form.amount || 0)) }} {{ form.currency }}</div>
        </div>
        <div>
          <div class="font-medium text-blue-800">Net Amount (Pre-VAT)</div>
          <div class="text-blue-600">{{ formatCurrency(netAmount) }} {{ form.currency }}</div>
        </div>
        <div>
          <div class="font-medium text-blue-800">Commission per Role (20%)</div>
          <div class="text-blue-600">{{ formatCurrency(commissionAmount) }} {{ form.currency }}</div>
        </div>
      </div>
      <p class="mt-4 text-sm text-blue-700">
        Two commission entries will be created: one for source ({{ formatCurrency(commissionAmount) }} {{ form.currency }}) 
        and one for account manager ({{ formatCurrency(commissionAmount) }} {{ form.currency }}).
        <span v-if="selectedCompanyId">
          If the selected company has tracking configured, commissions will be auto-mapped.
        </span>
        <span v-else>
          Since no company is selected, commissions will remain unmapped for manual assignment.
        </span>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted } from 'vue'
import axios from 'axios'

const props = defineProps({
  companies: Array,
  routes: Object
})

// Form data
const form = ref({
  amount: '',
  currency: 'EUR',
  payment_method: '',
  product_description: '',
  customer_email: '',
  customer_name: '',
  transaction_date: new Date().toISOString().split('T')[0],
  include_vat: true
})

const submitting = ref(false)

// Company search
const companySearchTerm = ref('')
const showCompanyDropdown = ref(false)
const highlightedCompanyIndex = ref(-1)
const filteredCompanies = ref([...props.companies])
const selectedCompanyId = ref(null)

// Computed values
const netAmount = computed(() => {
  const amount = parseFloat(form.value.amount || 0)
  return form.value.include_vat ? amount / 1.25 : amount
})

const commissionAmount = computed(() => {
  return netAmount.value * 0.20
})

// Methods
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

const filterCompanies = () => {
  if (!companySearchTerm.value) {
    filteredCompanies.value = [...props.companies]
  } else {
    filteredCompanies.value = props.companies.filter(company =>
      company.name.toLowerCase().includes(companySearchTerm.value.toLowerCase())
    )
  }
  highlightedCompanyIndex.value = -1
}

const selectCompany = (company) => {
  companySearchTerm.value = company.name
  selectedCompanyId.value = company.id
  showCompanyDropdown.value = false
}

const selectFirstCompanyMatch = () => {
  if (filteredCompanies.value.length > 0) {
    const company = highlightedCompanyIndex.value >= 0 
      ? filteredCompanies.value[highlightedCompanyIndex.value]
      : filteredCompanies.value[0]
    selectCompany(company)
  }
}

const navigateCompanyDown = () => {
  if (highlightedCompanyIndex.value < filteredCompanies.value.length - 1) {
    highlightedCompanyIndex.value++
  }
}

const navigateCompanyUp = () => {
  if (highlightedCompanyIndex.value > 0) {
    highlightedCompanyIndex.value--
  }
}

const closeCompanyDropdown = () => {
  showCompanyDropdown.value = false
  highlightedCompanyIndex.value = -1
}

const handleCompanyBlur = () => {
  nextTick(() => {
    showCompanyDropdown.value = false
    highlightedCompanyIndex.value = -1
  })
}

const submitForm = async () => {
  submitting.value = true
  
  try {
    const formData = {
      ...form.value,
      company_id: selectedCompanyId.value
    }
    
    await axios.post(props.routes.store, formData)
    
    // Redirect to index on success
    window.location.href = props.routes.index + '?success=Transaction created successfully'
    
  } catch (error) {
    console.error('Error creating transaction:', error)
    
    if (error.response?.status === 422) {
      // Handle validation errors
      const errors = error.response.data.errors
      let errorMessage = 'Please correct the following errors:\n'
      Object.keys(errors).forEach(field => {
        errorMessage += `- ${errors[field][0]}\n`
      })
      alert(errorMessage)
    } else {
      alert('Error creating transaction. Please try again.')
    }
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  // Focus on first input
  document.getElementById('company_id')?.focus()
})
</script>